import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  referencesApi,
  type WorkspaceReference,
} from "../../api/references.api";
import {
  referenceGroups,
  referenceLabels,
  referenceSearchHints,
  CARD_MIME,
} from "./reference-catalog";
export function ReferencePicker({
  scope,
  disabled,
  onAdd,
}: {
  scope: string[];
  disabled: boolean;
  onAdd: (ref: WorkspaceReference) => void;
}) {
  const [entityType, setEntityType] =
    useState<WorkspaceReference["entityType"]>("CANDIDATE");
  const [text, setText] = useState(""),
    [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(text.trim()), 300);
    return () => clearTimeout(timer);
  }, [text]);
  const query = useQuery({
    queryKey: ["board-card-search", ...scope, entityType, search],
    queryFn: () => referencesApi.search(search, entityType),
    enabled: search.length >= 2,
    gcTime: 0,
    refetchInterval: 30000,
    retry: false,
  });
  return (
    <section className="workspace-card-picker">
      <h3>Objets métier</h3>
      <p>
        Recherchez un objet puis ajoutez son lien ou glissez-le sur le contenu.
        Les informations restent dans leur dossier source.
      </p>
      <label>
        Type de dossier
        <select
          className="doc-form__input"
          value={entityType}
          onChange={(e) => {
            setEntityType(e.target.value as WorkspaceReference["entityType"]);
            setSearch(text.trim());
          }}
        >
          {referenceGroups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.types.map((type) => (
                <option key={type} value={type}>
                  {referenceLabels[type]}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <div className="workspace-actions workspace-reference-search">
        <label>
          Rechercher un dossier
          <input
            className="doc-form__input"
            placeholder={`${referenceSearchHints[entityType]} (2 caractères minimum)`}
            minLength={2}
            maxLength={100}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <button
          type="button"
          onClick={() => {
            setSearch(text.trim());
            if (text.trim() === search) void query.refetch();
          }}
          className="btn btn--secondary"
          disabled={disabled || text.trim().length < 2}
        >
          Rechercher
        </button>
      </div>
      {text.trim().length < 2 && (
        <p>
          Saisissez au moins 2 caractères pour rechercher un dossier de cette
          école.
        </p>
      )}
      {query.isFetching && <p role="status">Recherche en cours…</p>}
      {query.isError ? (
        <p role="alert">Recherche indisponible. Réessayez.</p>
      ) : (
        !query.isFetching &&
        query.data && (
          <>
            <ul className="workspace-card-results">
              {query.data.items
                .filter((r) => r.available)
                .map(
                  (r) =>
                    r.available && (
                      <li
                        key={r.entityId}
                        draggable={!disabled}
                        onDragStart={(e) => {
                          e.dataTransfer.setData(
                            CARD_MIME,
                            JSON.stringify({
                              entityType: r.entityType,
                              entityId: r.entityId,
                            }),
                          );
                          e.dataTransfer.effectAllowed = "copy";
                        }}
                      >
                        <span>{r.label}</span>
                        <button
                          type="button"
                          className="btn btn--secondary"
                          disabled={disabled}
                          onClick={() =>
                            onAdd({
                              entityType: r.entityType,
                              entityId: r.entityId,
                            })
                          }
                        >
                          Ajouter la carte de {r.label}
                        </button>
                      </li>
                    ),
                )}
            </ul>
            {!query.data.items.length && <p>Aucun dossier accessible.</p>}
            {query.data.hasMore && (
              <p>
                Affinez la recherche : seuls les 20 premiers résultats sont
                affichés.
              </p>
            )}
          </>
        )
      )}
    </section>
  );
}
